import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../assets/header.png";
import { normalizeHomeSettings } from "../utils/homeContent";
import { normalizeSiteChromeSettings } from "../utils/siteChrome";
import { RUNTIME_PUBLIC_BASE, RUNTIME_API_BASE } from "../utils/runtimeBase";

const API = RUNTIME_PUBLIC_BASE;
const API_V1 = RUNTIME_API_BASE;
const HOME_SETTINGS_CACHE_KEY = "public-home-settings-v1";
const SITE_CHROME_CACHE_KEY = "public-site-chrome-v1";
const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

function Header() {
  const navigate = useNavigate();
  const [homeSettings, setHomeSettings] = useState(null);
  const [siteChrome, setSiteChrome] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const noticeTrackRef = useRef(null);
  const [marqueeDuration, setMarqueeDuration] = useState(7);

  useEffect(() => {
    let active = true;

    Promise.allSettled([
      axios.get(`${API_V1}/home-settings`, {
        params: { _ts: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      }),
      axios.get(`${API_V1}/site-chrome-settings`, {
        params: { _ts: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      }),
    ])
      .then((results) => {
        if (!active) return;
        const homeRes = results[0];
        const chromeRes = results[1];
        const cachedHome = safeParse(localStorage.getItem(HOME_SETTINGS_CACHE_KEY) || "");
        const cachedChrome = safeParse(localStorage.getItem(SITE_CHROME_CACHE_KEY) || "");
        const nextHome = homeRes.status === "fulfilled"
          ? normalizeHomeSettings(homeRes.value.data)
          : (cachedHome ? normalizeHomeSettings(cachedHome) : normalizeHomeSettings({}));
        const nextChrome = chromeRes.status === "fulfilled"
          ? normalizeSiteChromeSettings(chromeRes.value.data)
          : (cachedChrome ? normalizeSiteChromeSettings(cachedChrome) : normalizeSiteChromeSettings({}));
        setHomeSettings(nextHome);
        setSiteChrome(nextChrome);
        localStorage.setItem(HOME_SETTINGS_CACHE_KEY, JSON.stringify(nextHome));
        localStorage.setItem(SITE_CHROME_CACHE_KEY, JSON.stringify(nextChrome));
        setLoaded(true);
      })
      .catch(() => {
        if (!active) return;
        const cachedHome = safeParse(localStorage.getItem(HOME_SETTINGS_CACHE_KEY) || "");
        const cachedChrome = safeParse(localStorage.getItem(SITE_CHROME_CACHE_KEY) || "");
        setHomeSettings(
          cachedHome ? normalizeHomeSettings(cachedHome) : normalizeHomeSettings({})
        );
        setSiteChrome(
          cachedChrome ? normalizeSiteChromeSettings(cachedChrome) : normalizeSiteChromeSettings({})
        );
        setLoaded(true);
      });

    return () => {
      active = false;
    };
  }, []);

  const noticeText = String(siteChrome?.topbar_notice || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();
  const finalNotice = noticeText || "An ISO 9001:2015 Certified & MSME Certified Organization";
  useEffect(() => {
    const track = noticeTrackRef.current;
    if (!track) return undefined;

    const updateDuration = () => {
      const cycleDistance = Math.max(1, track.scrollWidth / 2);
      const pixelsPerSecond = 50;
      const nextDuration = Math.max(4, Math.round((cycleDistance / pixelsPerSecond) * 10) / 10);
      setMarqueeDuration(nextDuration);
    };

    updateDuration();
    window.addEventListener("resize", updateDuration);

    return () => {
      window.removeEventListener("resize", updateDuration);
    };
  }, [finalNotice]);

  if (!loaded || !homeSettings || !siteChrome) {
    return null;
  }

  const headerImage = homeSettings.banner_image ? `${API}/uploads/${homeSettings.banner_image}` : logo;

  return (
    <header className="site-header">
      <div className="site-topbar">
        <div className="site-topbar__inner">
          <div className="site-topbar__meta" style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
            <div className="site-topbar__notice-marquee">
              <div
                ref={noticeTrackRef}
                className="site-topbar__notice-track"
                style={{ "--topbar-marquee-duration": `${marqueeDuration}s` }}
              >
                <span>{finalNotice}</span>
                <span aria-hidden="true">{finalNotice}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="site-brandbar">
        <div className="site-brandbar__inner">
          <button
            type="button"
            className="site-brandbar__logo"
            onClick={() => navigate("/")}
            aria-label="Go to home page"
            style={{
              "--header-image-height-desktop": `${homeSettings.header_image_height_desktop || 140}px`,
              "--header-image-height-mobile": `${homeSettings.header_image_height_mobile || 100}px`,
            }}
          >
            <img
              src={headerImage}
              alt="NIDCS logo"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              data-eager-media="true"
            />
          </button>
        </div>
      </div>
    </header>
  );
}

export default Header;



